import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faKey, faCopy, faCheckCircle } from '@fortawesome/free-solid-svg-icons'
import { Link } from 'react-router-dom'
import { useState } from 'react'

export default function ApiDocs() {
  const [copiedItem, setCopiedItem] = useState<string | null>(null)

  const handleCopy = (text: string, itemName: string) => {
    navigator.clipboard.writeText(text)
    setCopiedItem(itemName)
    setTimeout(() => setCopiedItem(null), 2000)
  }

  const apiEndpoints = [
    {
      method: 'POST',
      path: '/api/partner/register-device',
      description: 'Register a device to a customer',
      request: {
        email: 'customer@example.com',
        deviceId: 123,
      },
      response: {
        message: 'Device registered successfully',
        registrationToken: 'abc123...',
        pendingAccount: {
          id: 'user-id',
          email: 'customer@example.com',
          role: 'CUSTOMER',
          expiresAt: '2025-12-31T23:59:59.000Z',
        },
      },
    },
    {
      method: 'GET',
      path: '/api/partner/devices?page=1&limit=10',
      description: 'List all devices',
      response: {
        devices: [
          {
            id: 123,
            deviceName: 'RIC-001234',
            wisolId: '0012AB',
            locationId: 1,
            createdAt: '2025-01-01T00:00:00.000Z',
          },
        ],
        pagination: {
          page: 1,
          limit: 10,
          total: 25,
          totalPages: 3,
        },
      },
    },
    {
      method: 'GET',
      path: '/api/partner/users?page=1&limit=10&status=all',
      description: 'List all customers/users',
      response: {
        users: [
          {
            id: 'user-id',
            email: 'customer@example.com',
            status: 'active',
            role: 'CUSTOMER',
            createdAt: '2025-01-01T00:00:00.000Z',
          },
        ],
        pagination: {
          page: 1,
          limit: 10,
          total: 15,
          totalPages: 2,
        },
      },
    },
    {
      method: 'POST',
      path: '/api/partner/users/:userId/resend-invitation',
      description: 'Resend invitation to a pending user',
      response: {
        message: 'Invitation resent successfully',
        newToken: 'xyz789...',
      },
    },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <FontAwesomeIcon icon={faKey} className="h-6 w-6 mr-2 text-blue-600" />
          Partner API Documentation
        </h1>
        <p className="mt-1 text-sm text-gray-600">Use your Partner API key to integrate with the IoT platform</p>
      </div>

      {/* API Key */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-blue-900 mb-2">Your API Key</h2>
        <p className="text-sm text-blue-800">
          Keys are generated and revoked on the{' '}
          <Link to="/settings/api-keys" className="font-medium underline">
            API Keys
          </Link>{' '}
          page. A key's full value is shown once, at the moment you create it — it's stored
          hashed, so it can be replaced but never re-displayed.
        </p>
        <p className="mt-3 text-sm text-blue-800">
          <strong>Note:</strong> Keep your API key secure. Do not share it publicly or commit it to version control.
        </p>
      </div>

      {/* Authentication */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Authentication</h2>
        <p className="text-sm text-gray-700 mb-4">
          All Partner API requests require authentication using your Partner API key in the Authorization header:
        </p>
        <div className="relative">
          <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
            <code>{`Authorization: Bearer YOUR_PARTNER_API_KEY`}</code>
          </pre>
          <button
            onClick={() => handleCopy('Authorization: Bearer YOUR_PARTNER_API_KEY', 'auth')}
            className="absolute top-2 right-2 px-3 py-1.5 bg-gray-700 text-white rounded text-xs hover:bg-gray-600"
          >
            <FontAwesomeIcon icon={copiedItem === 'auth' ? faCheckCircle : faCopy} className="h-3 w-3" />
          </button>
        </div>
      </div>

      {/* API Endpoints */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-gray-900">API Endpoints</h2>

        {apiEndpoints.map((endpoint, index) => {
          const curlCommand =
            endpoint.method === 'GET'
              ? `curl -X GET "${process.env.REACT_APP_BASE_URL_API || 'https://ioag-prod-api.azurewebsites.net/api'}${
                  endpoint.path
                }" \\
  -H "Authorization: Bearer YOUR_PARTNER_API_KEY"`
              : `curl -X POST "${process.env.REACT_APP_BASE_URL_API || 'https://ioag-prod-api.azurewebsites.net/api'}${
                  endpoint.path
                }" \\
  -H "Authorization: Bearer YOUR_PARTNER_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '${JSON.stringify(endpoint.request, null, 2)}'`

          return (
            <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <span
                      className={`px-2.5 py-0.5 rounded text-xs font-semibold ${
                        endpoint.method === 'GET' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {endpoint.method}
                    </span>
                    <code className="text-sm font-mono text-gray-900">{endpoint.path}</code>
                  </div>
                  <p className="text-sm text-gray-600">{endpoint.description}</p>
                </div>
              </div>

              {/* Request Body */}
              {endpoint.request && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Request Body:</h4>
                  <pre className="bg-gray-50 p-4 rounded border border-gray-200 overflow-x-auto text-xs">
                    <code>{JSON.stringify(endpoint.request, null, 2)}</code>
                  </pre>
                </div>
              )}

              {/* Response */}
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Response:</h4>
                <pre className="bg-gray-50 p-4 rounded border border-gray-200 overflow-x-auto text-xs">
                  <code>{JSON.stringify(endpoint.response, null, 2)}</code>
                </pre>
              </div>

              {/* cURL Example */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-2">cURL Example:</h4>
                <div className="relative">
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded overflow-x-auto text-xs">
                    <code>{curlCommand}</code>
                  </pre>
                  <button
                    onClick={() => handleCopy(curlCommand, `curl-${index}`)}
                    className="absolute top-2 right-2 px-3 py-1.5 bg-gray-700 text-white rounded text-xs hover:bg-gray-600"
                  >
                    <FontAwesomeIcon
                      icon={copiedItem === `curl-${index}` ? faCheckCircle : faCopy}
                      className="h-3 w-3"
                    />
                  </button>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
